import { Card } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import type { AdminGatewayAccount, AdminGatewayAccountListFilters } from '@/api/models';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { CustomDataGrid, type DataGridColumn } from '@/components/shell/CustomDataGrid';
import { EmptyState } from '@/components/shell/EmptyState';
import { ErrorState } from '@/components/shell/ErrorState';
import { LoadingState } from '@/components/shell/LoadingState';
import { StatusChip } from '@/components/shell/StatusChip';
import { payApiClient } from '@/api/client';
import { useApiResource } from '@/hooks/useApiResource';
import { gatewayAccountModeTone, gatewayAccountStatusTone } from './domain';

interface GatewayAccountsListViewProps {
  filters: AdminGatewayAccountListFilters;
  onFiltersChange: (patch: Partial<AdminGatewayAccountListFilters>) => void;
}

export function GatewayAccountsListView({ filters, onFiltersChange }: GatewayAccountsListViewProps) {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApiResource(
    () => payApiClient.listAdminGatewayAccounts(filters),
    JSON.stringify(filters),
  );

  if (loading) {
    return (
      <Card>
        <LoadingState variant="list" rows={8} />
      </Card>
    );
  }

  if (error) {
    if (error.errorCode === 'access_denied') {
      return (
        <Card>
          <AccessDenied />
        </Card>
      );
    }
    return (
      <Card>
        <ErrorState title={translate('pay.gatewayAccounts.list.errorTitle')} body={error.message} onRetry={reload} />
      </Card>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <Card>
        <EmptyState title={translate('pay.gatewayAccounts.list.emptyTitle')} body={translate('pay.gatewayAccounts.list.emptyBody')} />
      </Card>
    );
  }

  const columns: Array<DataGridColumn<AdminGatewayAccount>> = [
    {
      key: 'tenant',
      label: translate('pay.gatewayAccounts.list.columnTenant'),
      mono: 'code',
      render: (item) => item.tenantCode,
    },
    {
      key: 'gateway',
      label: translate('pay.gatewayAccounts.list.columnGateway'),
      render: (item) => translate(`pay.gatewayAccounts.gateway.${item.gateway}`),
    },
    {
      key: 'alias',
      label: translate('pay.gatewayAccounts.list.columnAlias'),
      render: (item) => item.alias,
    },
    {
      key: 'mode',
      label: translate('pay.gatewayAccounts.list.columnMode'),
      render: (item) => (
        <StatusChip label={translate(`pay.gatewayAccounts.mode.${item.mode}`)} tone={gatewayAccountModeTone(item.mode)} />
      ),
    },
    {
      key: 'status',
      mobileVisible: true,
      label: translate('pay.gatewayAccounts.list.columnStatus'),
      render: (item) => (
        <StatusChip label={translate(`pay.gatewayAccounts.status.${item.status}`)} tone={gatewayAccountStatusTone(item.status)} />
      ),
    },
    {
      key: 'createdAt',
      label: translate('pay.gatewayAccounts.list.columnCreatedAt'),
      render: (item) => new Date(item.createdAt).toLocaleString('es-ES'),
    },
  ];

  return (
    <CustomDataGrid
      columns={columns}
      rows={data.items}
      getRowKey={(item) => item.id}
      onRowClick={(item) => navigate(`/gateway-accounts/${encodeURIComponent(item.id)}`)}
      rowAriaLabel={translate('pay.gatewayAccounts.list.viewDetail')}
      pagination={{
        page: data.page.page,
        pageSize: data.page.pageSize,
        totalItems: data.page.totalItems,
        rowsPerPageLabel: translate('pay.gatewayAccounts.list.rowsPerPage'),
        onPageChange: (page) => onFiltersChange({ page }),
        onPageSizeChange: (pageSize) => onFiltersChange({ pageSize, page: 1 }),
      }}
    />
  );
}
